import React, { createContext, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { StyleSheet, Dimensions, PixelRatio } from 'react-native';
import { Device } from 'DeviceUtils';
import { APP_THEME } from '../options';

// Base building unit - see assets
const BU = 16;

const { width, height } = Dimensions.get('window');

export const normalize = (size, multiplier = 2) => {
    const scale = (width / height) * multiplier;

    const newSize = size * scale;

    return Math.round(PixelRatio.roundToNearestPixel(newSize));
};

export const Theme = {
    BU,
    // Android for some reason crops some pixels from header
    appHeaderHeight: Device.isIos ? 54 : 62,
    appHeaderBackgroundColor: '#000000',
    playerHeaderHeight: Device.isPhone ? BU * 3 : BU * 5,
    sixteenByNineRatio: 16 / 9,
    twentySevenByFortyOneRatio: 27 / 41,

    // Poiter color
    timelineColor: {
        adUnskippable: '#B30707',
        adSkippable: '#82D166',
        noRestriction: '#EBEBEB',
        background: '#FFFFFF',
        backgroundInactive: 'rgba(255, 255, 255, 0.3)',
        default: '#FFFFFF'
    },

    // Timemeline current time color
    timeProgressColor: {
        primaryBackgroundColor: '#E20074'
    },

    // Font sizes
    fontSizes: {
        title: Device.isTabletOrWeb ? 30 : 28,
        subtitle: Device.isTabletOrWeb ? 21 : 18,
        button: Device.isTabletOrWeb ? 21 : 23,
        description: 18,
        episodes: Device.isTabletOrWeb ? 21 : 18,
        sectionValue: 21
    },

    statusBarTheme: 'light-content',
    regularFont: 'TeleGroteskNext-Regular',
    thinFont: 'TeleGroteskNext-Thin',
    boldFont: 'TeleGroteskNext-Bold',
    ultraFont: 'TeleGroteskNext-Ultra',
    mediumFont: 'TeleGroteskNext-Medium',

    primaryFontSize: 18,
    primaryMargin: Device.isTabletOrWeb ? BU * 2 : BU,
    primaryPaddingBottom: Device.isTabletOrWeb ? BU * 2 : BU * 3,
    minPromoCarouselHeight: 350,
    playerFooterHeight: BU * 3,
    backgroundImageUri: null,
    // Default values
    primaryBackgroundColor: '#000000',
    secondaryBackgroundColor: '#100E12',
    primaryTextColor: '#EBEBEB',
    secondaryTextColor: '#A4A4A4',
    lightThemeTextColor: '#4B4B4B',
    labelTextColor: '#7C7C7C',
    alertTextColor: '#262626',
    primaryActiveColor: '#E20074',
    primaryButtonBackgroundColor: '#E20074',
    primaryButtonBorderColor: '#B2005C',
    primaryLinkColor: '#00A0DE',
    activeColor: '#00A0DE',
    secondaryButtonBackgroundColor: '#262626',
    secondaryButtonBorderColor: '#6C6C6C',
    positiveColor: '#335a08',
    negativeColor: '#6d0001',
    placeholderTextColor: '#6C6C6C',
    switchTrueColor: '#6BB324',
    switchFalseColor: '#EDEDED',
    ...APP_THEME
};

export const fontStyles = {
    heading1: {
        height: 30,
        width: 101,
        color: '#EBEBEB',
        fontFamily: Theme.regularFont,
        fontSize: Device.isTabletOrWeb ? 24 : 16,
        letterSpacing: 0.4,
        lineHeight: 30
    },
    heading2: {
        height: 26,
        width: 'auto',
        color: '#EBEBEB',
        fontFamily: Theme.regularFont,
        fontSize: Device.isTabletOrWeb ? 21 : 14,
        letterSpacing: 0.7,
        lineHeight: 30
    },
    heading3: {
        height: 26,
        width: 92,
        color: '#EBEBEB',
        fontFamily: Theme.ultraFont,
        fontSize: Device.isTabletOrWeb ? 21 : 14,
        letterSpacing: 0.7,
        lineHeight: 26
    },
    heading4: {
        color: '#EBEBEB',
        fontFamily: Theme.regularFont,
        fontSize: Device.isTabletOrWeb ? 15 : 10,
        letterSpacing: 0.83,
        lineHeight: 24
    },
    heading5: {
        height: 40,
        width: 198,
        color: '#EBEBEB',
        fontFamily: Theme.ultraFont,
        fontSize: Device.isTabletOrWeb ? 40 : 26,
        fontWeight: 900,
        letterSpacing: 1.15,
        lineHeight: 40
    },
    heading6: {
        height: 26,
        width: 129,
        color: '#EBEBEB',
        fontFamily: Theme.ultraFont,
        fontSize: Device.isTabletOrWeb ? 26 : 17,
        fontWeight: 900,
        letterSpacing: 0.75,
        lineHeight: 26
    },
    heading7: {
        height: 36,
        width: 119,
        color: '#EBEBEB',
        fontFamily: Theme.regularFont,
        fontSize: Device.isTabletOrWeb ? 30 : 20,
        letterSpacing: -0.3,
        lineHeight: 36
    },
    settingsHeading: {
        color: Theme.labelTextColor,
        fontFamily: Theme.boldFont,
        fontSize: Device.isTabletOrWeb ? 18 : 19,
        letterSpacing: 0.83
    },
    body: {
        color: '#EBEBEB',
        fontFamily: Theme.regularFont,
        fontSize: Device.isTabletOrWeb ? 18 : 12
    },
    secondaryText: {
        color: '#A4A4A4',
        fontFamily: Theme.regularFont,
        fontSize: Device.isTabletOrWeb ? 15 : 10,
        letterSpacing: 0.4,
        lineHeight: 20
    },
    activeTab: {
        height: 20,
        width: 84,
        color: '#EBEBEB',
        fontFamily: Theme.boldFont,
        fontSize: Device.isTabletOrWeb ? 15 : 10,
        fontWeight: 'bold',
        letterSpacing: 1,
        lineHeight: 20,
        textAlign: 'center'
    },
    inactiveTab: {
        height: 20,
        width: 84,
        color: '#EBEBEB',
        fontFamily: Theme.regularFont,
        fontSize: Device.isTabletOrWeb ? 15 : 10,
        fontWeight: 'bold',
        letterSpacing: 1,
        lineHeight: 20,
        textAlign: 'center'
    },
    title: {
        color: '#EBEBEB',
        fontFamily: Theme.regularFont,
        fontSize: Device.isTabletOrWeb ? 30 : 25
    },
    subtitle: {
        color: '#A4A4A4',
        fontFamily: Theme.regularFont,
        fontSize: Device.isTabletOrWeb ? 21 : 18
    },
    navigationHeader: {
        fontFamily: Theme.ultraFont,
        fontWeight: Device.isIos ? '900' : 'normal',
        fontSize: 26,
        alignSelf: 'center'
    }
};

export const globalStyles = StyleSheet.create({
    title: {
        fontFamily: Theme.regularFont,
        color: Theme.primaryTextColor,
        fontSize: Theme.fontSizes.title,
        marginBottom: 16,
        textAlign: 'center'
    },
    secondaryDescription: {
        fontFamily: Theme.regularFont,
        color: Theme.secondaryTextColor,
        fontSize: 20,
        textAlign: 'center',
        paddingHorizontal: 10
    },
    primaryTextColor: {
        fontFamily: Theme.regularFont,
        fontSize: 20,
        textAlign: 'center',
        paddingHorizontal: 10,
        color: 'white'
    }
});

export const androidContentOffset = Device.isAndroid ? { paddingBottom: 35 } : {};

export const ThemeContext = createContext(Theme);

export const ThemeProvider = ({ children }) => {
    const [theme, setTheme] = useState(Theme);

    return (
        <ThemeContext.Provider
            value={{
                theme,
                setTheme
            }}
        >
            {children}
        </ThemeContext.Provider>
    );
};

ThemeProvider.defaultProps = {
    children: []
};

ThemeProvider.propTypes = {
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node])
};
export const useTheme = () => useContext(ThemeContext);

export const withTheme = (Component) => (props) => (
    <ThemeContext.Consumer>{(theme) => <Component {...props} {...theme} />}</ThemeContext.Consumer>
);
